import type { TFunction } from 'i18next';

import type { MenuItem } from '@/types/menu.types';

export const isMenuItemVisible = (
  item: MenuItem,
  isAuthenticated: boolean,
): boolean => {
  if (!item.showInMenu) return false;

  const conditions = item.conditions;
  if (!conditions) return true;

  if (
    conditions.isAuthenticated !== undefined &&
    conditions.isAuthenticated !== isAuthenticated
  ) {
    return false;
  }

  if (conditions.customCondition && !conditions.customCondition()) {
    return false;
  }

  return true;
};

export const getVisibleMenuItems = (
  items: MenuItem[],
  isAuthenticated: boolean,
): MenuItem[] =>
  items.reduce<MenuItem[]>((visible, item) => {
    if (!isMenuItemVisible(item, isAuthenticated)) return visible;

    if (!item.children) {
      visible.push(item);
      return visible;
    }

    const children = getVisibleMenuItems(item.children, isAuthenticated);
    if (children.length > 0 || item.path) {
      visible.push({ ...item, children });
    }
    return visible;
  }, []);

export const flattenMenuItems = (items: MenuItem[]): MenuItem[] =>
  items.flatMap((item) => {
    const leaves = item.children ? flattenMenuItems(item.children) : [];
    return item.path ? [item, ...leaves] : leaves;
  });

export const findMenuItemWithParent = (
  items: MenuItem[],
  path: string,
  isAuthenticated: boolean,
  parent: MenuItem | null = null,
): { item: MenuItem; parent: MenuItem | null } | null => {
  for (const item of items) {
    if (item.path === path && isMenuItemVisible(item, isAuthenticated)) {
      return { item, parent };
    }
    if (item.children) {
      const found = findMenuItemWithParent(
        item.children,
        path,
        isAuthenticated,
        item,
      );
      if (found) return found;
    }
  }
  return null;
};

export const getMenuLabel = (item: MenuItem, t: TFunction): string =>
  item.translationKey ? t(item.translationKey) : item.label;
